import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel, LinearProgress } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import SolarInverterIpsPanel from './SolarInverterIpsPanel';

const CARD_BG = '#1a1a1a';
const BLUE = '#2391FF';

interface DiscoverRow {
  ip?: string;
  nombre?: string;
  serial?: number;
  result: 'added' | 'updated_ip' | 'already_known' | 'error';
  detail?: string;
}

interface SolarDiscoveryTabProps {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
}

const SolarDiscoveryTab: React.FC<SolarDiscoveryTabProps> = ({ isDemoUser = false, onDemoActionAttempt }) => {
  const { t, i18n } = useTranslation();
  const [discovering, setDiscovering] = useState(false);
  const [rows, setRows] = useState<DiscoverRow[]>([]);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(localStorage.getItem('lastSolarDiscovery'));
  const [refreshKey, setRefreshKey] = useState(0);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/solar/ai-management').then((res) => {
      if (!cancelled) setAiEnabled(Boolean(res.data?.enabled));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleAi = async (next: boolean) => {
    if (isDemoUser) {
      onDemoActionAttempt?.();
      return;
    }
    setAiSaving(true);
    const prev = aiEnabled;
    setAiEnabled(next);
    try {
      const res = await api.put('/solar/ai-management', { enabled: next });
      setAiEnabled(Boolean(res.data?.enabled));
    } catch {
      setAiEnabled(prev);
    } finally {
      setAiSaving(false);
    }
  };

  const handleDiscover = async () => {
    if (isDemoUser) {
      onDemoActionAttempt?.();
      return;
    }
    setDiscovering(true);
    setError(null);
    setRows([]);
    setSummary('');
    try {
      const response = await api.post('/solar/discover', null, { timeout: 120000 });
      const data = response.data || {};
      const built: DiscoverRow[] = [
        ...(data.added || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, serial: e.serial, result: 'added' as const })),
        ...(data.updated_ip || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, serial: e.serial, result: 'updated_ip' as const })),
        ...(data.already_known || []).map((e: any) => ({ ip: e.ip, nombre: e.nombre, serial: e.serial, result: 'already_known' as const })),
        ...(data.errors || []).map((e: any) => ({ ip: e.ip, result: 'error' as const, detail: e.error })),
      ];
      setRows(built);
      const added = built.filter((r) => r.result === 'added').length;
      const updated = built.filter((r) => r.result === 'updated_ip').length;
      const known = built.filter((r) => r.result === 'already_known').length;
      setSummary(t('settings.solarDiscovery.summary', { added, updated, known }));
      const now = new Date();
      const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
      const formatted = now.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      setLastRun(formatted);
      localStorage.setItem('lastSolarDiscovery', formatted);
      setRefreshKey((n) => n + 1);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.solarDiscovery.errorDiscover'));
    } finally {
      setDiscovering(false);
    }
  };

  const resultLabel = (r: DiscoverRow) => {
    switch (r.result) {
      case 'added': return { text: `✓ ${t('settings.solarDiscovery.resultAdded')}`, color: '#66bb6a' };
      case 'updated_ip': return { text: `↻ ${t('settings.solarDiscovery.resultUpdated')}`, color: '#e5a54b' };
      case 'already_known': return { text: `— ${t('settings.solarDiscovery.resultKnown')}`, color: '#999' };
      default: return { text: `✕ ${r.detail || t('settings.solarDiscovery.resultError')}`, color: '#e57373' };
    }
  };

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2, touchAction: 'pan-y' }}>
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', m: 0 }}
          control={(
            <Checkbox
              size="small"
              checked={aiEnabled}
              disabled={aiSaving}
              onChange={(ev) => { void handleAi(ev.target.checked); }}
              sx={{ color: '#888', '&.Mui-checked': { color: BLUE }, pt: 0.25 }}
            />
          )}
          label={(
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                {t('settings.solarDiscovery.aiManagement.label')}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#999', lineHeight: 1.4 }}>
                {t('settings.solarDiscovery.aiManagement.hint')}
              </Typography>
            </Box>
          )}
        />
      </Box>
      <Box sx={{ backgroundColor: CARD_BG, borderRadius: 1, p: 1.5, mb: 1.5, border: '1px solid #333' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {t('settings.solarDiscovery.discoverTitle')}
          </Typography>
          {lastRun && (
            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
              {t('settings.solarDiscovery.lastRun')}: {lastRun}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 1.25, lineHeight: 1.4 }}>
          {t('settings.solarDiscovery.help')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon />}
          disabled={discovering}
          onClick={handleDiscover}
          sx={{ backgroundColor: BLUE, textTransform: 'none', '&:hover': { backgroundColor: '#1a7ae0' } }}
        >
          {discovering ? t('settings.solarDiscovery.discovering') : t('settings.solarDiscovery.discoverBtn')}
        </Button>
        {discovering && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              sx={{ height: 6, borderRadius: 3, backgroundColor: '#333', '& .MuiLinearProgress-bar': { backgroundColor: BLUE } }}
            />
          </Box>
        )}
        {error && <Typography sx={{ color: '#e57373', fontSize: '0.8rem', mt: 1 }}>{error}</Typography>}
        {summary && <Typography sx={{ fontSize: '0.8rem', color: BLUE, mt: 1 }}>{summary}</Typography>}
        {rows.map((r, i) => {
          const label = resultLabel(r);
          return (
            <Box key={`${r.ip}-${r.serial}-${i}`} sx={{ display: 'flex', gap: 1, py: 0.35, borderBottom: '1px solid #262626' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#fff', width: '38%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.nombre || r.ip || '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#888', width: '28%' }}>{r.ip || '—'}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: label.color, width: '34%' }}>{label.text}</Typography>
            </Box>
          );
        })}
      </Box>
      <SolarInverterIpsPanel
        isDemoUser={isDemoUser}
        onDemoActionAttempt={onDemoActionAttempt}
        refreshKey={refreshKey}
      />
    </Box>
  );
};

export default SolarDiscoveryTab;
